import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, AutoComplete, Row, Col } from "antd";
import { useParams } from "react-router-dom";
import { doPost } from "../../../../../../../../API/apis";
import { useStateGlobalContext } from "../../../../../../../../contexts/GlobalContext";
import { useStateInvoicesContext } from "../../../../../../../../contexts/InvoiceContext";
import Notification from "../../../../../../../../components/Notification/Notification";
import { useAppointmentSearchServices } from "../../../../../../../../API/apiUtils";
import {
  validatePrice,
  validateQuantity,
} from "../../../../../../../../utils/validator";

const CustomInvoice1 = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [saving, setSaving] = useState(false);
  const [isServiceTypeDisabled, setIsServiceTypeDisabled] = useState(false);
  const [isServiceGroupDisabled, setIsServiceGroupDisabled] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const { patientBasicInfo, getPatientData, getInvoices, getUnpaidInvoicesCount } =
    useStateGlobalContext();
  const { options, handleSearchServices } = useAppointmentSearchServices();
  // const { getUnpaidInvoicesCount } = useStateInvoicesContext();

  const { first_name, middle_name, last_name } = patientBasicInfo || {};

  const fullName = `${first_name || ""} ${middle_name || ""} ${
    last_name || ""
  }`;

  const handleCustomInvoice = async (values) => {
    setSaving(true);
    const quantity = Number(values.quantity) || 1;
    const serviceNameWithQuantity = values.service_name;
    const calculatedTotal = Number(values.price) * quantity;

    const data = {
      patient_name: fullName,
      service_name: serviceNameWithQuantity,
      service_type: values?.service_type,
      service_group: values?.service_group,
      price: Number(values.price),
      quantity: quantity,
      sub_total: calculatedTotal,
    };
    try {
      const response = await doPost(`/invoice/invoice-add/${id}/`, data);
      if (response.status === 201) {
        Notification.success("Invoice Created Successfully");
        getUnpaidInvoicesCount(id);
        getInvoices(id);
        handleCancel();
      }
    } catch (error) {
      Notification.warning("Something Went Wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
    setIsServiceTypeDisabled(false);
    setIsServiceGroupDisabled(false);
    setTotalPrice(0);
  };

  const handleSelect = (value) => {
    const selectedOption = options.find((option) => option.value === value);
    if (selectedOption) {
      form.setFieldsValue({ 
        price: selectedOption.price,
        service_name: selectedOption?.service_name,
        service_type: selectedOption?.service_type,
        service_group: selectedOption?.service_group
      }); 
      setIsServiceTypeDisabled(true);
      setIsServiceGroupDisabled(true);
      // Calculate total price when service is selected
      const quantity = form.getFieldValue('quantity') || 1;
      setTotalPrice(Number(selectedOption.price) * quantity);
    }
  };

  const handleChange = (value) => {
    const isOptionSelected = options.some((option) => option.value === value);
    if (!isOptionSelected) {
      setIsServiceTypeDisabled(false); // Enable the service type input if not selected from options
      setIsServiceGroupDisabled(false); // Enable the service group input if not selected from options
      form.setFieldsValue({ 
        price: undefined,
        service_type: undefined,
        service_group: undefined
      }); // Clear the fields
      setTotalPrice(0); // Reset total price
    }
  };

  // Function to calculate total price when price or quantity changes
  const calculateTotal = () => {
    const price = form.getFieldValue('price');
    const quantity = form.getFieldValue('quantity') || 1;
    if (price && !isNaN(price)) {
      setTotalPrice(Number(price) * Number(quantity));
    } else {
      setTotalPrice(0);
    }
  };

  // Handle price change
  const handlePriceChange = (e) => {
    const price = e.target.value;
    form.setFieldsValue({ price });
    calculateTotal();
  };

  // Handle quantity change
  const handleQuantityChange = (e) => {
    const quantity = e.target.value;
    form.setFieldsValue({ quantity });
    calculateTotal();
  };

  useEffect(() => {
    getPatientData(id);
  }, []);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title="Create Custom Invoice"
      closeIcon={
        <Button size="small" className="app-close-icon" shape="round">
          Close
        </Button>
      }
    >
      <Form form={form} onFinish={handleCustomInvoice} layout="vertical">
        <Form.Item
          label="Service Name"
          name="service_name"
          rules={[
            {
              required: true,
              message: "Please Enter Service Name",
            },
          ]}
        >
          <AutoComplete
            onSearch={handleSearchServices}
            options={options}
            placeholder="Search Service Type"
            maxLength={100}
            allowClear
            onSelect={handleSelect}
            onChange={handleChange}
          />
        </Form.Item>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item label="Service Type" name="service_type">
              <Input placeholder="Service Type" maxLength={30} disabled={isServiceTypeDisabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Service Group" name="service_group">
              <Input placeholder="Service Group" maxLength={30} disabled={isServiceGroupDisabled} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label="Quantity"
              name="quantity"
              tooltip="Default Quantity is 1 if not selected"
              rules={[
                {
                  required: true,
                  message: "Please Enter Quantity",
                },
                {
                  validator: validateQuantity,
                },
              ]}
            >
              <Input placeholder="Quantity" maxLength={10} onChange={handleQuantityChange} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Price"
              name="price"
              rules={[
                {
                  required: true,
                  message: "Please Enter Price",
                },
                {
                  validator: validatePrice,
                },
              ]}
            >
              <Input
                placeholder="Price"
                maxLength={10}
                onChange={handlePriceChange}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Total Price Display */}
        {totalPrice > 0 && (
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col span={24}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#f6f8fa', 
                border: '1px solid #e1e4e8',
                borderRadius: '6px',
                textAlign: 'right'
              }}>
                <strong style={{ fontSize: '16px', color: '#24292e' }}>
                  Total: ${totalPrice.toFixed(2)}
                </strong>
              </div>
            </Col>
          </Row>
        )}

        <div className="flex justify-end">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            className="ml-2"
            htmlType="submit"
            loading={saving}
          >
            Create Custom Invoice
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CustomInvoice1;
